import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FaChartBar, FaUsers, FaUserGraduate, FaSpinner, FaChevronDown, FaTimes, FaCheck } from 'react-icons/fa';
import { Student, Dimension } from '../types';
import StudentDimensionsChart from './shared/StudentDimensionsChart';
import { filterDimensionsByCourse } from '../utils/dimensionFilters';
import { getValidLevelsCount, getValidLevelsAchieved, calculateProgressByAgeTramos } from '../utils/gradeCalculationForWithdrawal';
import {
  deriveAnchoredAge,
  getSixYearRangeForAge,
  createAgeTicksAndLabels,
  chartOffsetForMinAge,
  calculateExpectedPercentage,
  calculatePreviousYearPercentage,
  BASE_MIN_AGE,
  BASE_MAX_AGE,
  COURSE_TO_AGE_MAP
} from '../utils/levelCalculations';
import { 
  ComposedChart,
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Scatter,
  Rectangle,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

interface SimplifiedAnalyticsProps {
  students: Student[];
  dimensionsTemplate: Dimension[];
  role: 'admin' | 'teacher';
  courses: string[];
}

type TabType = 'level' | 'course' | 'student';
type ChartType = 'bar' | 'radar';

// Componente MultiSelect personalizado
interface MultiSelectProps {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ 
  options, 
  selectedValues, 
  onChange, 
  placeholder = "Seleccionar...",
  label,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar el dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(v => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const removeValue = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== option));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      {/* Campo de selección */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-[42px] px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 flex flex-wrap gap-1">
            {selectedValues.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : (
              selectedValues.map(value => (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-sm"
                >
                  {value}
                  <button
                    onClick={(e) => removeValue(value, e)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </span>
              ))
            )}
          </div>
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown de opciones */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-4 py-2 text-gray-500 text-sm">No hay opciones disponibles</div>
          ) : (
            options.map(option => (
              <div
                key={option}
                onClick={() => toggleOption(option)}
                className={`px-4 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                  selectedValues.includes(option) ? 'bg-blue-50' : ''
                }`}
              >
                <span className="text-sm">{option}</span>
                {selectedValues.includes(option) && (
                  <FaCheck className="text-blue-600" />
                )}
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Contador */}
      {selectedValues.length > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          {selectedValues.length} seleccionado(s)
        </p>
      )}
    </div>
  );
};

// Componente MultiSelectStudent para selección de estudiantes
interface MultiSelectStudentProps {
  students: Student[];
  selectedStudents: Student[];
  onChange: (students: Student[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

const MultiSelectStudent: React.FC<MultiSelectStudentProps> = ({ 
  students, 
  selectedStudents, 
  onChange, 
  placeholder = "Seleccionar...",
  label,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleStudent = (student: Student) => {
    const isSelected = selectedStudents.some(s => s.id === student.id);
    if (isSelected) {
      onChange(selectedStudents.filter(s => s.id !== student.id));
    } else {
      onChange([...selectedStudents, student]);
    }
  };

  const removeStudent = (student: Student, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedStudents.filter(s => s.id !== student.id));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-[42px] px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 flex flex-wrap gap-1">
            {selectedStudents.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : (
              selectedStudents.map(student => (
                <span
                  key={student.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-sm"
                >
                  {student.name}
                  <button
                    onClick={(e) => removeStudent(student, e)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </span>
              ))
            )}
          </div>
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {students.length === 0 ? (
            <div className="px-4 py-2 text-gray-500 text-sm">No hay estudiantes disponibles</div>
          ) : (
            students.map(student => {
              const isSelected = selectedStudents.some(s => s.id === student.id);
              return (
                <div
                  key={student.id}
                  onClick={() => toggleStudent(student)}
                  className={`px-4 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                    isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-sm">{student.name}</span>
                  {isSelected && (
                    <FaCheck className="text-blue-600" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      
      {selectedStudents.length > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          {selectedStudents.length} seleccionado(s)
        </p>
      )}
    </div>
  );
};

const COLORS = {
  achieved: '#4A90E2',
  expected: '#F5A623',
  previousYear: '#6B7280'
};

// Componente para rectángulo vertical (scatter para nivel año anterior)
const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  const width = 4;
  const height = 40;
  
  return (
    <Rectangle
      x={cx - 1}
      y={cy - height / 2}
      width={width}
      height={height}
      fill={fill}
    />
  );
};

const SimplifiedAnalytics: React.FC<SimplifiedAnalyticsProps> = ({
  students,
  dimensionsTemplate,
  role,
  courses
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('level');
  const [selectedCourse, setSelectedCourse] = useState<string>(courses[0] || '');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  
  // Arrays para comparación múltiple
  const [selectedLevelsForComparison, setSelectedLevelsForComparison] = useState<string[]>([]);
  const [selectedCoursesForComparison, setSelectedCoursesForComparison] = useState<string[]>([]);
  const [selectedStudentsForComparison, setSelectedStudentsForComparison] = useState<Student[]>([]);

  // Efecto para seleccionar automáticamente el primer curso cuando los cursos se carguen
  useEffect(() => {
    if (courses.length > 0 && !selectedCourse) {
      console.log('📊 SimplifiedAnalytics: Seleccionando automáticamente el primer curso:', courses[0]);
      setSelectedCourse(courses[0]);
    }
  }, [courses, selectedCourse]);

  // Función para extraer el nivel base del curso (ej: "Medio Mayor A" -> "Medio Mayor")
  const extractBaseLevel = (courseName: string): string => {
    // Eliminar letras al final (A, B, C, D, etc.) y espacios extras
    return courseName.replace(/\s+[A-Z]$/, '').trim();
  };

  // Función para ordenar niveles cronológicamente
  const sortLevelsByOrder = (levels: string[]): string[] => {
    const levelOrder = [
      'Medio Mayor',
      'Pre Kinder',
      'Kinder',
      'Primero básico',
      'Segundo básico',
      'Tercero básico',
      'Cuarto básico',
      'Quinto básico',
      'Sexto básico',
      'Séptimo básico',
      'Octavo básico'
    ];

    return levels.sort((a, b) => {
      const indexA = levelOrder.indexOf(a);
      const indexB = levelOrder.indexOf(b);
      
      // Si ambos están en el orden definido
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // Si solo A está en el orden definido
      if (indexA !== -1) return -1;
      // Si solo B está en el orden definido
      if (indexB !== -1) return 1;
      // Si ninguno está en el orden definido, ordenar alfabéticamente
      return a.localeCompare(b);
    });
  };

  // Calcular niveles únicos
  const uniqueLevels = useMemo(() => {
    const levels = new Set<string>();
    courses.forEach(course => {
      levels.add(extractBaseLevel(course));
    });
    return sortLevelsByOrder(Array.from(levels));
  }, [courses]);

  // Efecto para seleccionar automáticamente el primer nivel
  useEffect(() => {
    if (uniqueLevels.length > 0 && !selectedLevel) {
      console.log('📊 SimplifiedAnalytics: Seleccionando automáticamente el primer nivel:', uniqueLevels[0]);
      setSelectedLevel(uniqueLevels[0]);
    }
  }, [uniqueLevels, selectedLevel]);

  // Efecto para controlar el estado de carga
  useEffect(() => {
    if (courses.length > 0 && students.length > 0 && selectedCourse) {
      // Simular un pequeño delay para que el gráfico se renderice completamente
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [courses, students, selectedCourse]);

  // Filtrar estudiantes por curso seleccionado
  const studentsInCourse = useMemo(() => {
    return students.filter(s => s.course === selectedCourse);
  }, [students, selectedCourse]);

  // Calcular datos agregados por nivel (agrupando cursos del mismo nivel)
  const levelAggregateData = useMemo(() => {
    if (!selectedLevel) {
      return { data: [], minAge: BASE_MIN_AGE, maxAge: BASE_MAX_AGE, offsetValue: 0 };
    }

    // Filtrar todos los cursos que pertenecen al nivel seleccionado
    const coursesInLevel = courses.filter(course => extractBaseLevel(course) === selectedLevel);
    const studentsInLevel = students.filter(s => coursesInLevel.includes(s.course));

    if (studentsInLevel.length === 0) {
      return { data: [], minAge: BASE_MIN_AGE, maxAge: BASE_MAX_AGE, offsetValue: 0 };
    }

    // Calcular edades para el rango del gráfico
    const ages = studentsInLevel.map(student => 
      deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true })
    );
    const minAge = Math.min(...ages);
    const maxAge = Math.max(...ages);
    const range = getSixYearRangeForAge((minAge + maxAge) / 2);
    const offsetValue = chartOffsetForMinAge(range.minAge);

    // Filtrar dimensiones según el curso
    const dimensionsForChart = filterDimensionsByCourse(dimensionsTemplate, studentsInLevel[0].course);

    // Calcular datos para cada dimensión
    const data = dimensionsForChart.map(dim => {
      // 🎯 NUEVO SISTEMA: Calcular nivel logrado promedio usando tramos de edad
      const studentProgresses = studentsInLevel.map(student => {
        // Usar el nuevo sistema de cálculo por tramos
        const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, student.course, student.skillLevels);
        
        // 🎯 Calcular posición en el eje X basándose en tramos completados
        const UNITS_PER_TRAMO = 20;
        const BASE_UNITS = 20; // 3 años = 20 unidades
        let scaledLogrado = BASE_UNITS;
        tramoResult.tramoDetails.forEach((tramo) => {
          scaledLogrado += tramo.fraction * UNITS_PER_TRAMO;
        });
        return scaledLogrado;
      });
      const avgAchievedLevel = studentProgresses.reduce((sum, val) => sum + val, 0) / studentProgresses.length;

      // Calcular nivel esperado promedio
      const expectedLevels = studentsInLevel.map(student => {
        const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
        return calculateExpectedPercentage(age);
      });
      const avgExpectedLevel = expectedLevels.reduce((sum, val) => sum + val, 0) / expectedLevels.length;

      // Calcular nivel año anterior promedio
      // Usa datos históricos si están disponibles, sino usa cálculo por edad
      const previousYearLevels = studentsInLevel.map(student => {
        const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
        
        // Check if student has historical previousYearLevels data
        const historicalData = (student as any).previousYearLevels;
        if (historicalData && Array.isArray(historicalData) && historicalData.length > 0) {
          const historicalLevels = historicalData
            .filter((h: any) => h.dimensionId === dim.id)
            .map((h: any) => ({ dimensionId: h.dimensionId, skillId: h.skillId, level: h.level }));
          
          if (historicalLevels.length > 0) {
            // Estimar curso anterior para el denominador
            const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base => student.course && student.course.includes(base));
            const currentAge = COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
            const prevAge = Math.max(3, currentAge - 1);
            const prevCourse = Object.entries(COURSE_TO_AGE_MAP).find(([_, age]) => age === prevAge)?.[0] || "Medio Mayor";
            
            const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, prevCourse, historicalLevels);
            
            const UNITS_PER_TRAMO = 20;
            const BASE_UNITS = 20;
            let scaledHistorical = BASE_UNITS;
            tramoResult.tramoDetails.forEach((tramo) => {
              scaledHistorical += tramo.fraction * UNITS_PER_TRAMO;
            });
            return scaledHistorical;
          }
        }
        // Fallback to age-based calculation
        return calculatePreviousYearPercentage(anchoredAge);
      });
      const avgPreviousYearLevel = previousYearLevels.reduce((sum, val) => sum + val, 0) / previousYearLevels.length;

      return {
        name: dim.name,
        percentageAchieved: Math.max(0, avgAchievedLevel - offsetValue),
        expectedLevel: Math.max(0, avgExpectedLevel - offsetValue),
        previousYearLevel: Math.max(0, avgPreviousYearLevel - offsetValue)
      };
    });

    console.log(`📊 SimplifiedAnalytics - levelAggregateData para ${selectedLevel}:`, {
      coursesInLevel,
      studentsCount: studentsInLevel.length,
      dimensionsCount: data.length,
      minAge,
      maxAge,
      offsetValue
    });

    return {
      data,
      minAge: range.minAge,
      maxAge: range.maxAge,
      offsetValue
    };
  }, [selectedLevel, students, courses, dimensionsTemplate]);

  // Calcular datos para múltiples niveles (comparación)
  const multipleLevelsAggregateData = useMemo(() => {
    // Si no hay niveles seleccionados para comparar, usar el nivel actual
    const levelsToProcess = selectedLevelsForComparison.length > 0 
      ? selectedLevelsForComparison 
      : (selectedLevel ? [selectedLevel] : []);

    return levelsToProcess.map(level => {
      const coursesInLevel = courses.filter(course => extractBaseLevel(course) === level);
      const studentsInLevel = students.filter(s => coursesInLevel.includes(s.course));

      if (studentsInLevel.length === 0) {
        return { level, data: [], minAge: BASE_MIN_AGE, maxAge: BASE_MAX_AGE, offsetValue: 0 };
      }

      const ages = studentsInLevel.map(student => 
        deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true })
      );
      const minAge = Math.min(...ages);
      const maxAge = Math.max(...ages);
      const range = getSixYearRangeForAge((minAge + maxAge) / 2);
      const offsetValue = chartOffsetForMinAge(range.minAge);

      const dimensionsForChart = filterDimensionsByCourse(dimensionsTemplate, studentsInLevel[0].course);

      const data = dimensionsForChart.map(dim => {
        // 🎯 NUEVO SISTEMA: Calcular usando tramos de edad
        const studentProgresses = studentsInLevel.map(student => {
          const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, student.course, student.skillLevels);
          
          // 🎯 Calcular posición en el eje X basándose en tramos completados
          const UNITS_PER_TRAMO = 20;
          const BASE_UNITS = 20;
          let scaledLogrado = BASE_UNITS;
          tramoResult.tramoDetails.forEach((tramo) => {
            scaledLogrado += tramo.fraction * UNITS_PER_TRAMO;
          });
          return scaledLogrado;
        });
        const avgAchievedLevel = studentProgresses.reduce((sum, val) => sum + val, 0) / studentProgresses.length;

        const expectedLevels = studentsInLevel.map(student => {
          const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          return calculateExpectedPercentage(age);
        });
        const avgExpectedLevel = expectedLevels.reduce((sum, val) => sum + val, 0) / expectedLevels.length;

        const previousYearLevels = studentsInLevel.map(student => {
          const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          const historicalData = (student as any).previousYearLevels;
          
          if (historicalData && Array.isArray(historicalData) && historicalData.length > 0) {
            const historicalLevels = historicalData
              .filter((h: any) => h.dimensionId === dim.id)
              .map((h: any) => ({ dimensionId: h.dimensionId, skillId: h.skillId, level: h.level }));
            
            if (historicalLevels.length > 0) {
              const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base => student.course && student.course.includes(base));
              const currentAge = COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
              const prevAge = Math.max(3, currentAge - 1);
              const prevCourse = Object.entries(COURSE_TO_AGE_MAP).find(([_, age]) => age === prevAge)?.[0] || "Medio Mayor";
              
              const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, prevCourse, historicalLevels);
              
              const UNITS_PER_TRAMO = 20;
              const BASE_UNITS = 20;
              let scaledHistorical = BASE_UNITS;
              tramoResult.tramoDetails.forEach((tramo) => {
                scaledHistorical += tramo.fraction * UNITS_PER_TRAMO;
              });
              return scaledHistorical;
            }
          }
          return calculatePreviousYearPercentage(anchoredAge);
        });
        const avgPreviousYearLevel = previousYearLevels.reduce((sum, val) => sum + val, 0) / previousYearLevels.length;

        return {
          name: dim.name,
          percentageAchieved: Math.max(0, avgAchievedLevel - offsetValue),
          expectedLevel: Math.max(0, avgExpectedLevel - offsetValue),
          previousYearLevel: Math.max(0, avgPreviousYearLevel - offsetValue)
        };
      });

      return {
        level,
        data,
        minAge: range.minAge,
        maxAge: range.maxAge,
        offsetValue
      };
    });
  }, [selectedLevelsForComparison, selectedLevel, students, courses, dimensionsTemplate]);

  // Calcular datos para múltiples cursos (comparación)
  const multipleCoursesAggregateData = useMemo(() => {
    const coursesToProcess = selectedCoursesForComparison.length > 0 
      ? selectedCoursesForComparison 
      : (selectedCourse ? [selectedCourse] : []);

    return coursesToProcess.map(course => {
      const courseStudents = students.filter(s => s.course === course);
      if (courseStudents.length === 0) return { course, data: [], minAge: BASE_MIN_AGE, maxAge: BASE_MAX_AGE, offsetValue: 0 };

      const ages = courseStudents.map(student => 
        deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true })
      );
      const minAge = Math.min(...ages);
      const maxAge = Math.max(...ages);
      const range = getSixYearRangeForAge((minAge + maxAge) / 2);
      const offsetValue = chartOffsetForMinAge(range.minAge);

      const dimensionsForChart = filterDimensionsByCourse(dimensionsTemplate, course);

      const data = dimensionsForChart.map(dim => {
        // 🎯 NUEVO SISTEMA: Calcular usando tramos de edad
        const studentAchievedLevels = courseStudents.map(student => {
          const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, student.course, student.skillLevels);
          
          // 🎯 Calcular posición en el eje X basándose en tramos completados
          const UNITS_PER_TRAMO = 20;
          const BASE_UNITS = 20;
          let scaledLogrado = BASE_UNITS;
          tramoResult.tramoDetails.forEach((tramo) => {
            scaledLogrado += tramo.fraction * UNITS_PER_TRAMO;
          });
          return scaledLogrado;
        });
        const avgAchievedLevel = studentAchievedLevels.reduce((sum, val) => sum + val, 0) / studentAchievedLevels.length;

        const expectedLevels = courseStudents.map(student => {
          const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          return calculateExpectedPercentage(age);
        });
        const avgExpectedLevel = expectedLevels.reduce((sum, val) => sum + val, 0) / expectedLevels.length;

        const previousYearLevels = courseStudents.map(student => {
          const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          const historicalData = (student as any).previousYearLevels;
          
          if (historicalData && Array.isArray(historicalData) && historicalData.length > 0) {
            const historicalLevels = historicalData
              .filter((h: any) => h.dimensionId === dim.id)
              .map((h: any) => ({ dimensionId: h.dimensionId, skillId: h.skillId, level: h.level }));
            
            if (historicalLevels.length > 0) {
              const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base => student.course && student.course.includes(base));
              const currentAge = COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
              const prevAge = Math.max(3, currentAge - 1);
              const prevCourse = Object.entries(COURSE_TO_AGE_MAP).find(([_, age]) => age === prevAge)?.[0] || "Medio Mayor";
              
              const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, prevCourse, historicalLevels);
              
              const UNITS_PER_TRAMO = 20;
              const BASE_UNITS = 20;
              let scaledHistorical = BASE_UNITS;
              tramoResult.tramoDetails.forEach((tramo) => {
                scaledHistorical += tramo.fraction * UNITS_PER_TRAMO;
              });
              return scaledHistorical;
            }
          }
          return calculatePreviousYearPercentage(anchoredAge);
        });
        const avgPreviousYearLevel = previousYearLevels.reduce((sum, val) => sum + val, 0) / previousYearLevels.length;

        return {
          name: dim.name,
          percentageAchieved: Math.max(0, avgAchievedLevel - offsetValue),
          expectedLevel: Math.max(0, avgExpectedLevel - offsetValue),
          previousYearLevel: Math.max(0, avgPreviousYearLevel - offsetValue)
        };
      });

      return { course, data, minAge: range.minAge, maxAge: range.maxAge, offsetValue };
    });
  }, [selectedCoursesForComparison, selectedCourse, students, dimensionsTemplate]);

  // Funciones para manejar comparación múltiple - YA NO SE USAN (se manejan directamente en los onChange)

  const renderCourseView = () => {
    return (
      <div className="space-y-6">
        {/* Selectores */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelect
              label="Seleccionar Cursos:"
              options={courses}
              selectedValues={selectedCoursesForComparison}
              onChange={(values) => {
                setSelectedCoursesForComparison(values);
                if (values.length > 0) {
                  setSelectedCourse(values[0]);
                }
              }}
              placeholder="Selecciona uno o más cursos..."
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Visualización:
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="bar">Gráfico de Barras</option>
                <option value="radar">Gráfico de Radar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Gráficos en Grid */}
        <div className={`grid gap-6 ${multipleCoursesAggregateData.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {multipleCoursesAggregateData.map(({ course, data, minAge, maxAge, offsetValue }) => (
            <div key={course} className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Progreso Promedio del Curso: {course}
              </h3>
              
              {chartType === 'bar' ? (
                // Gráfico de Barras Horizontales
                <ResponsiveContainer width="100%" height={Math.max(500, data.length * 60)}>
                  <ComposedChart
                    layout="vertical"
                    data={data}
                    margin={{ top: 20, right: 30, left: 200, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis 
                      type="number" 
                      domain={[0, (maxAge - minAge) * 20 + 20]}
                      ticks={(() => {
                        const { ticks } = createAgeTicksAndLabels(minAge, maxAge);
                        return ticks;
                      })()}
                      tickFormatter={(value) => {
                        const { labels } = createAgeTicksAndLabels(minAge, maxAge);
                        return labels[value] ? `Hacia los ${labels[value]}` : '';
                      }}
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                      axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      tickLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      interval={0}
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={180}
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                      axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      tickLine={false}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const realValue = (value as number) + offsetValue;
                        return [`${realValue.toFixed(1)}`, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="percentageAchieved" 
                      fill={COLORS.achieved}
                      name="Nivel Logrado"
                      barSize={40}
                      radius={[2, 2, 2, 2]}
                    />
                    <Bar
                      dataKey="expectedLevel"
                      fill={COLORS.expected}
                      name="Nivel Esperado" 
                      barSize={6}
                      radius={[2, 2, 2, 2]}
                    />
                    <Scatter 
                      dataKey="previousYearLevel" 
                      fill={COLORS.previousYear}
                      name="Nivel Año Anterior"
                      shape={<CustomVerticalRectangle />}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                // Gráfico de Radar
                <ResponsiveContainer width="100%" height={600}>
                  <RadarChart
                    data={data}
                    margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
                  >
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, (maxAge - minAge) * 20 + 20]}
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      tickFormatter={(value) => {
                        const realValue = value + offsetValue;
                        const age = (realValue - 20) / 20 + BASE_MIN_AGE;
                        return `${Math.floor(age)}`;
                      }}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const realValue = (value as number) + offsetValue;
                        const age = (realValue - 20) / 20 + BASE_MIN_AGE;
                        return [`Edad: ${age.toFixed(1)} años`, name];
                      }}
                    />
                    <Legend />
                    <Radar 
                      name="Nivel Logrado" 
                      dataKey="percentageAchieved" 
                      stroke={COLORS.achieved} 
                      fill={COLORS.achieved} 
                      fillOpacity={0.6}
                      strokeWidth={2}
                    />
                    <Radar 
                      name="Nivel Esperado" 
                      dataKey="expectedLevel" 
                      stroke={COLORS.expected} 
                      fill={COLORS.expected} 
                      fillOpacity={0.3}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                    <Radar 
                      name="Nivel Año Anterior" 
                      dataKey="previousYearLevel" 
                      stroke={COLORS.previousYear} 
                      fill={COLORS.previousYear} 
                      fillOpacity={0.2}
                      strokeWidth={2}
                      strokeDasharray="3 3"
                    />
                  </RadarChart>
                </ResponsiveContainer>
              )}
              
              {/* Leyenda explicativa - solo mostrar en el último gráfico */}
              {course === multipleCoursesAggregateData[multipleCoursesAggregateData.length - 1].course && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded mt-1 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Logrado:</strong> Promedio del nivel actual alcanzado por los estudiantes del curso
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-0.5 bg-yellow-500 mt-2 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Esperado:</strong> Nivel esperado según la edad y curso
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-3 bg-gray-500 mt-1 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Año Anterior:</strong> Referencia del nivel del año anterior
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLevelView = () => {
    return (
      <div className="space-y-6">
        {/* Selectores */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelect
              label="Seleccionar Niveles:"
              options={uniqueLevels}
              selectedValues={selectedLevelsForComparison}
              onChange={(values) => {
                setSelectedLevelsForComparison(values);
                if (values.length > 0) {
                  setSelectedLevel(values[0]);
                }
              }}
              placeholder="Selecciona uno o más niveles..."
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Visualización:
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="bar">Gráfico de Barras</option>
                <option value="radar">Gráfico de Radar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Gráficos en Grid */}
        <div className={`grid gap-6 ${multipleLevelsAggregateData.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {multipleLevelsAggregateData.map(({ level, data, minAge, maxAge, offsetValue }) => (
            <div key={level} className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Progreso Promedio del Nivel: {level}
              </h3>
              
              {chartType === 'bar' ? (
                // Gráfico de Barras Horizontales
                <ResponsiveContainer width="100%" height={Math.max(500, data.length * 60)}>
                  <ComposedChart
                    layout="vertical"
                    data={data}
                    margin={{ top: 20, right: 30, left: 200, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis 
                      type="number" 
                      domain={[0, (maxAge - minAge) * 20 + 20]}
                      ticks={(() => {
                        const { ticks } = createAgeTicksAndLabels(minAge, maxAge);
                        return ticks;
                      })()}
                      tickFormatter={(value) => {
                        const { labels } = createAgeTicksAndLabels(minAge, maxAge);
                        return labels[value] ? `Hacia los ${labels[value]}` : '';
                      }}
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                      axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      tickLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      interval={0}
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={180}
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                      axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
                      tickLine={false}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const realValue = (value as number) + offsetValue;
                        return [`${realValue.toFixed(1)}`, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="percentageAchieved" 
                      fill={COLORS.achieved}
                      name="Nivel Logrado"
                      barSize={40}
                      radius={[2, 2, 2, 2]}
                    />
                    <Bar
                      dataKey="expectedLevel"
                      fill={COLORS.expected}
                      name="Nivel Esperado" 
                      barSize={6}
                      radius={[2, 2, 2, 2]}
                    />
                    <Scatter 
                      dataKey="previousYearLevel" 
                      fill={COLORS.previousYear}
                      name="Nivel Año Anterior"
                      shape={<CustomVerticalRectangle />}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                // Gráfico de Radar
                <ResponsiveContainer width="100%" height={600}>
                  <RadarChart
                    data={data}
                    margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
                  >
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, (maxAge - minAge) * 20 + 20]}
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      tickFormatter={(value) => {
                        const realValue = value + offsetValue;
                        const age = (realValue - 20) / 20 + BASE_MIN_AGE;
                        return `${Math.floor(age)}`;
                      }}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const realValue = (value as number) + offsetValue;
                        const age = (realValue - 20) / 20 + BASE_MIN_AGE;
                        return [`Edad: ${age.toFixed(1)} años`, name];
                      }}
                    />
                    <Legend />
                    <Radar 
                      name="Nivel Logrado" 
                      dataKey="percentageAchieved" 
                      stroke={COLORS.achieved} 
                      fill={COLORS.achieved} 
                      fillOpacity={0.6}
                      strokeWidth={2}
                    />
                    <Radar 
                      name="Nivel Esperado" 
                      dataKey="expectedLevel" 
                      stroke={COLORS.expected} 
                      fill={COLORS.expected} 
                      fillOpacity={0.3}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                    <Radar 
                      name="Nivel Año Anterior" 
                      dataKey="previousYearLevel" 
                      stroke={COLORS.previousYear} 
                      fill={COLORS.previousYear} 
                      fillOpacity={0.2}
                      strokeWidth={2}
                      strokeDasharray="3 3"
                    />
                  </RadarChart>
                </ResponsiveContainer>
              )}
              
              {/* Leyenda explicativa - solo mostrar en el último gráfico */}
              {level === multipleLevelsAggregateData[multipleLevelsAggregateData.length - 1].level && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded mt-1 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Logrado:</strong> Promedio del nivel actual alcanzado por todos los estudiantes del nivel
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-0.5 bg-yellow-500 mt-2 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Esperado:</strong> Nivel esperado según la edad promedio
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-3 bg-gray-500 mt-1 flex-shrink-0"></div>
                      <p className="text-gray-600">
                        <strong className="text-gray-700">Nivel Año Anterior:</strong> Referencia del nivel del año anterior
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStudentView = () => {
    console.log('📊 SimplifiedAnalytics - renderStudentView:', {
      selectedCourse,
      studentsInCourseCount: studentsInCourse.length,
      selectedStudent: selectedStudent?.name || 'ninguno'
    });

    return (
      <div className="space-y-6">
        {/* Selectores */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Curso:
              </label>
              <select
                value={selectedCourse}
                onChange={(e) => {
                  console.log('📊 Cambio de curso:', e.target.value);
                  setSelectedCourse(e.target.value);
                  setSelectedStudent(null);
                  setSelectedStudentsForComparison([]);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {courses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>
            <MultiSelectStudent
              label="Seleccionar Estudiantes:"
              students={studentsInCourse}
              selectedStudents={selectedStudentsForComparison}
              onChange={(students) => {
                console.log('📊 Estudiantes seleccionados:', students.map(s => s.name));
                setSelectedStudentsForComparison(students);
                if (students.length > 0) {
                  setSelectedStudent(students[0]);
                }
              }}
              placeholder="Selecciona uno o más estudiantes..."
              disabled={studentsInCourse.length === 0}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Visualización:
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="bar">Gráfico de Barras</option>
                <option value="radar">Gráfico de Radar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Gráficos en Grid */}
        {selectedStudentsForComparison.length > 0 ? (
          <div className={`grid gap-6 ${selectedStudentsForComparison.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
            {selectedStudentsForComparison.map(student => (
              <div key={student.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Progreso Individual: {student.name}
                </h3>
                <div className="mt-4">
                  <StudentDimensionsChart
                    student={student}
                    showLegend={selectedStudentsForComparison[selectedStudentsForComparison.length - 1].id === student.id}
                    height={700}
                    showSubdimensions={true}
                    showExpectedLevel={true}
                    showPreviousYearLevel={true}
                    chartType={chartType}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center">
            <FaUserGraduate className="mx-auto text-gray-400 text-5xl mb-4" />
            <p className="text-gray-600 text-lg">
              Selecciona uno o más estudiantes para ver su progreso
            </p>
            {studentsInCourse.length === 0 && (
              <p className="text-gray-500 text-sm mt-2">
                No hay estudiantes en el curso seleccionado
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Componente de carga
  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <FaChartBar className="text-blue-600 text-3xl" />
            <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center py-20">
          <FaSpinner className="text-blue-600 text-6xl animate-spin mb-6" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cargando Analytics...</h2>
          <p className="text-gray-600 text-center max-w-md">
            Estamos preparando los datos y gráficos para ti. Esto solo tomará unos segundos.
          </p>
          <div className="mt-8 flex gap-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <FaChartBar className="text-blue-600 text-3xl" />
          <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('level')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'level'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaChartBar />
            <span>Vista por Nivel</span>
          </button>
          <button
            onClick={() => setActiveTab('course')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'course'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaUsers />
            <span>Vista por Curso</span>
          </button>
          <button
            onClick={() => setActiveTab('student')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'student'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaUserGraduate />
            <span>Vista por Estudiante</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mt-6">
        {activeTab === 'level' && renderLevelView()}
        {activeTab === 'course' && renderCourseView()}
        {activeTab === 'student' && renderStudentView()}
      </div>
    </div>
  );
};

export default SimplifiedAnalytics;

